/**
 * Reward Center error parsing.
 *
 * Campaign Manager maps business failures — including not-found — to HTTP 400,
 * so the status alone is not enough: the stable `reason` string is what the UI
 * switches on. A response with no `reason` came from the gateway or the
 * authorization service, which answer with their own Persian message.
 */
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';

export const RewardCenterReasonValues = {
  USER_IDENTITY_MISSING: 'USER_IDENTITY_MISSING',
  CAMPAIGN_NOT_FOUND: 'CAMPAIGN_NOT_FOUND',
  TASK_NOT_FOUND: 'TASK_NOT_FOUND',
  TASK_NOT_IN_CAMPAIGN: 'TASK_NOT_IN_CAMPAIGN',
  CAMPAIGN_NOT_ACTIVE: 'CAMPAIGN_NOT_ACTIVE',
  TASK_NOT_ACTIVE: 'TASK_NOT_ACTIVE',
  TASK_NOT_SELF_COMPLETABLE: 'TASK_NOT_SELF_COMPLETABLE',
  TASK_NOT_COMPLETED: 'TASK_NOT_COMPLETED',
  TASK_NOT_CLAIMABLE: 'TASK_NOT_CLAIMABLE',
  TASK_ALREADY_CLAIMED: 'TASK_ALREADY_CLAIMED',
  TASK_CLAIM_WINDOW_CLOSED: 'TASK_CLAIM_WINDOW_CLOSED',
  INVALID_CYCLE: 'INVALID_CYCLE',
  INVALID_ARGUMENT: 'INVALID_ARGUMENT'
} as const;

export type RewardCenterReason =
  typeof RewardCenterReasonValues[keyof typeof RewardCenterReasonValues];

export interface RewardCenterError {
  /** HTTP status, or a fetch/parse error tag when the request never landed. */
  status: number | string;
  /** Stable machine reason; empty when the error came from the gateway. */
  reason: string;
  /** Persian text safe to show the user. */
  message: string;
}

const GENERIC_MESSAGE = 'خطایی رخ داد. لطفاً دوباره تلاش کنید.';

/** Copy per §12 of the integration guide, keyed by the stable reason. */
const REASON_MESSAGES: Record<string, string> = {
  [RewardCenterReasonValues.USER_IDENTITY_MISSING]:
    'نشست شما معتبر نیست. لطفاً دوباره وارد شوید.',
  [RewardCenterReasonValues.CAMPAIGN_NOT_FOUND]:
    'این کمپین دیگر در دسترس نیست.',
  [RewardCenterReasonValues.TASK_NOT_FOUND]: 'این چالش دیگر در دسترس نیست.',
  [RewardCenterReasonValues.TASK_NOT_IN_CAMPAIGN]:
    'این چالش به این کمپین تعلق ندارد.',
  [RewardCenterReasonValues.CAMPAIGN_NOT_ACTIVE]: 'این کمپین فعال نیست.',
  [RewardCenterReasonValues.TASK_NOT_ACTIVE]:
    'زمان انجام این چالش به پایان رسیده است.',
  [RewardCenterReasonValues.TASK_NOT_SELF_COMPLETABLE]:
    'این چالش به صورت خودکار بررسی می‌شود.',
  [RewardCenterReasonValues.TASK_NOT_COMPLETED]:
    'هنوز این چالش را کامل نکرده‌اید.',
  [RewardCenterReasonValues.TASK_NOT_CLAIMABLE]:
    'دریافت جایزه این چالش در حال حاضر ممکن نیست.',
  [RewardCenterReasonValues.TASK_ALREADY_CLAIMED]:
    'جایزه این چالش قبلاً دریافت شده است.',
  [RewardCenterReasonValues.TASK_CLAIM_WINDOW_CLOSED]:
    'مهلت دریافت جایزه این چالش به پایان رسیده است.',
  [RewardCenterReasonValues.INVALID_CYCLE]:
    'این دوره معتبر نیست. صفحه را بازخوانی کنید.',
  [RewardCenterReasonValues.INVALID_ARGUMENT]: 'درخواست نامعتبر است.'
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isFetchBaseQueryError = (error: unknown): error is FetchBaseQueryError =>
  isRecord(error) && 'status' in error;

/**
 * Normalizes anything RTK Query can hand back (business error, gateway error,
 * transport failure) into one shape the containers can branch on.
 */
export const parseRewardCenterError = (error: unknown): RewardCenterError => {
  if (!isFetchBaseQueryError(error)) {
    return {
      status: 'UNKNOWN',
      reason: '',
      message: GENERIC_MESSAGE
    };
  }

  // Transport-level failures (FETCH_ERROR / PARSING_ERROR / TIMEOUT_ERROR).
  if (typeof error.status === 'string') {
    return {
      status: error.status,
      reason: '',
      message:
        error.status === 'TIMEOUT_ERROR'
          ? 'پاسخی از سرور دریافت نشد. لطفاً دوباره تلاش کنید.'
          : 'ارتباط با سرور برقرار نشد. اتصال خود را بررسی کنید.'
    };
  }

  const body = isRecord(error.data) ? error.data : undefined;
  const reason = typeof body?.reason === 'string' ? body.reason : '';
  const serverMessage =
    typeof body?.message === 'string' && body.message.length > 0
      ? body.message
      : '';

  return {
    status: error.status,
    reason,
    // A known reason wins, then whatever the gateway said (already Persian),
    // then a generic fallback.
    message: REASON_MESSAGES[reason] || serverMessage || GENERIC_MESSAGE
  };
};

/** The session no longer identifies the user: they have to sign in again. */
export const isSessionError = (error: unknown): boolean =>
  parseRewardCenterError(error).reason ===
  RewardCenterReasonValues.USER_IDENTITY_MISSING;

/**
 * Claiming twice is not a failure — the reward is already the user's. Callers
 * fold this into the success path so a retried claim still flips the card.
 */
export const isAlreadyClaimedError = (error: unknown): boolean =>
  parseRewardCenterError(error).reason ===
  RewardCenterReasonValues.TASK_ALREADY_CLAIMED;

/**
 * Reasons that mean the cached task/campaign no longer matches the server, so
 * the list should be refetched rather than just showing a message.
 */
const STALE_STATE_REASONS: string[] = [
  RewardCenterReasonValues.CAMPAIGN_NOT_FOUND,
  RewardCenterReasonValues.TASK_NOT_FOUND,
  RewardCenterReasonValues.CAMPAIGN_NOT_ACTIVE,
  RewardCenterReasonValues.TASK_NOT_ACTIVE,
  RewardCenterReasonValues.TASK_NOT_CLAIMABLE,
  RewardCenterReasonValues.TASK_CLAIM_WINDOW_CLOSED,
  RewardCenterReasonValues.INVALID_CYCLE
];

export const shouldRefetchAfterError = (error: unknown): boolean =>
  STALE_STATE_REASONS.includes(parseRewardCenterError(error).reason);
